/* eslint-disable no-param-reassign */
/*
 Config and utilites for styling and positioning pages in PDF.
 Based loosely on how tailwind works with CSS.
 Styles and positions are configured here.
 Page templates can reference these fonts, colours, positions in the template.

 TODO:
 Refactor this into a pdfStyle.config.js,
 PDFStyler.js

 Then pdfStyle.config.js can be overwritten/customized in the PDFDocument
 */
import _ from 'lodash';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindCfgFile from '../../../tailwind.config';

const tailwindConfig = resolveConfig(tailwindCfgFile);

let textHeight = () => {
    console.warn('Setting height to default 1 line, should not be used.');
    return 'h-0-1/4';
};

// Defaults hard coded here.  Overridden by calling init() with a new config.
const defaults = {
    unit: 'pt',
    format: 'letter',
    width: 612,
    height: 792,
    margin: 45,
    footer: 35,
    header: 54,
    // number of width units per column
    // Used for positons if 2 columns, then 'col-2': x: w-4 + gutter, w: w-4
    widths: 4,
    columns: 2,
    // gutter is the width between columns
    gutter: 40,
    heights: 13,
    baseFont: {
        family: 'default',
        lineHeight: 'default',
        size: 'base',
        weight: 'normal',
        style: 'normal',
        colour: 'print.dark'
    },
    chartFill: 'neutral.1',
    // attributes to exclude as content nodes
    // Defines what the styler can use as attributes instead of content nodes
    attributes: ['position', 'font', 'selector', 'svg', 'svgData', 'data', 'img', 'text', 'styles', 'border', 'background', 'onload', 'sections', 'markdown']
};

// Gets set in the init() to defaults
const config = {};

// Fonts, positions that can be used by default.
const styles = {
    fontFamily: {
        default: 'Roboto'
    },
    fontSize: {
        '2xs': 4,
        xs: 6,
        sm: 8,
        base: 10,
        md: 12,
        lg: 14,
        xl: 18,
        '2xl': 24,
        '3xl': 32,
        '4xl': 36,
        '5xl': 40,
        '6xl': 48
    },
    lineHeightFactors: {
        default: 1.3,
        title: 1.15
    },
    // fontWeight/style here basically as reference for allowable values.
    // These must be defined in the index of the base64 font package
    fontWeight: {
        normal: 'normal',
        bold: 'bold',
        lighter: 'lighter'
    },
    fontStyle: {
        normal: 'normal',
        italic: 'italic'
    },
    font: {
        title: {
            size: '3xl'
        },
        background: {
            colour: 'print.light'
        },
        base: defaults.baseFont
    },
    position: {
        page: {
            x: 0,
            y: 0,
            w: 'w-full',
            h: 'h-full'
        },
        header: {
            x: 'margin',
            y: 0,
            w: 'w-content',
            h: 'h-header',
            // rh = default children row height
            // rh: 'h-0-1/4'
        },
        margin: {
            x: 0,
            y: 0,
            w: 'margin',
            h: 'h-full'
        },
        footer: {
            x: 'margin',
            y: 'y-footer',
            w: 'w-content',
            h: 'h-footer'
        },
        'col-1': {
            x: 'x-col-1',
            y: 'h-1',
            w: 'w-col',
            h: 'h-content',
            // rh: 'h-2'
        },
        'col-2': {
            x: 'x-col-2',
            y: 'h-1',
            w: 'w-col',
            h: 'h-content',
            // rh: 'h-2'
        },
        content: {
            x: 'margin',
            y: 'h-header',
            w: 'w-content',
            h: 'h-content'
        },
    }
};


const pageLayout = {
    id: 'test',
    grid: {
        position: 'content'
    },
    header: {
        border: { colour: 'neutral.2', width: 1 },
        text: 'header'
    },
    footer: {
        border: { colour: 'neutral.2', width: 1 },
        text: 'footer'
    },
    margin: {
        border: { colour: 'neutral.2', width: 1 },
        text: 'margin'
    },
    'col-1': {
        border: { colour: 'neutral.2', width: 1 },
        text: 'col-1',
        align: 'right'
    },
    'col-2': {
        border: { colour: 'neutral.2', width: 1 },
        text: 'col-2'
    },
    content: {
        border: { colour: 'neutral.1', width: 1.5 },
        text: 'content'
    }
};


// Colours shared with tailwind
_.assign(styles, { colours: tailwindConfig.theme.colors });


function mapColour(content) {
    const { colour } = content;
    if (!colour) {
        return colour;
    }
    const colourKey = colour.replace('text-', '').replace('-', '.');
    content.colour = _.get(styles.colours, colourKey) || colour;
    return content.colour;
}

// Gets value from spacings config
function mapSpacing(key) {
    // If it is in the styles, use it
    let val = _.get(styles.spacing, key) || key && +key;

    // If it is a number, use it.  If not, default to 0.
    if (!val) {
        val = +key;
        val = Number.isNaN(val) ? 0 : val;
    }
    return val;
}

// Maps all the properties of a container that can be set with spacing styles
function mapSpacings(content) {
    const { x, y, w, h, m, mt, mb, ml, mr } = content;
    content.x = mapSpacing(x);
    content.y = mapSpacing(y);
    content.w = mapSpacing(w);
    content.h = mapSpacing(h);
    const mAll = mapSpacing(m);
    content.mt = mapSpacing(mt) || mAll;
    content.mb = mapSpacing(mb) || mAll;
    content.mr = mapSpacing(mr) || mAll;
    content.ml = mapSpacing(ml) || mAll;
    return content;
}

function mapFixedPosition(content, parent, sibling) {
    // If no parent, has to be set to fixed
    if (!parent) {
        content.position = content.position || content.contentType || 'page';
    }
    const position = styles.position[content.position] || styles.position[content.contentType];
    if (position) {
        let fixedPosition = { ...position, fixed: content.position, position: content.position };
        fixedPosition = mapSpacings(fixedPosition);
        return fixedPosition;
    }
    // If a position is set, but not found, it is set to absolut on parent
    // So set position ='absolute', or position = 'anything' or position = 'parent' does same thing.
    if (content.position) {
        const { x, y, w, h } = parent;
        return { x, y, w, h, fixed: 'parent', position: 'absolute' };
    }

    // No position is set, and is a child, use relative positions
    return { relative: parent, inline: content.display === 'inline' && sibling };
}

function totalHeight(content) {
    return content.h + content.mt + content.mb;
}

function contentExpander(content) {
    // if content has a height set explicitly, it does not expand.
    if (!content.position.relative || content.h) {
        return () => {};
    }
    return (child, expandHeight) => {
        if (expandHeight === undefined) {
            expandHeight = totalHeight(child);
        }
        if (child.position.inline) {
            const sibHeight = totalHeight(child.position.inline);
            const childHeight = totalHeight(child);
            if (childHeight < sibHeight) {
                expandHeight = 0;
            } else {
                const oldHeight = childHeight - expandHeight;
                if (oldHeight < sibHeight) {
                    expandHeight = childHeight - sibHeight;
                }
            }
        }
        content.h += expandHeight;
        const parent = content.position.relative;
        if (parent) {
            parent.position.expander(content, expandHeight);
        }
    };
}


// Root nodes have mapped positions in the config, and are always fixed position
// Any other node is relative to the parent.
function positionContent(content, parent, sibling) {
    // If position is set, use the specified fixed position (header, content, etc.)
    content.position = mapFixedPosition(content, parent, sibling);
    content = mapSpacings(content);
    content.position.expander = contentExpander(content);
    // If content is fixed to a position, use it to position x, y and set width/h
    if (!content.position.relative) {
        content.x += content.position.x;
        content.y += content.position.y;
        content.w = content.w || content.position.w;
        content.h = content.h || content.position.h;
        // content.rh = content.rh || content.position.rh;
        return content;
    }
    // content.position = { relative: parent };
    // Reduce width based on new x
    content.w = content.w || parent.w - content.x;
    // If no fixed position, then position relative to parent and sibling

    // Calculate text height
    if (content.text) {
        content.h = content.h || textHeight(content);
    }
    content.x += parent.x + content.ml;
    content.y += content.mt;
    // let heightToParent = totalHeight(content);
    if (sibling) {
        // content.x = sibling.x;
        if (content.display === 'inline') {
            // content.x += sibling.w;
            content.x += sibling.w + sibling.mr;
            content.y += sibling.y;
            // const sibHeight = totalHeight(sibling);
            // console.log('sibHeight, heightToParent1', parent.contentType, content.contentType, sibling.contentType, sibHeight, heightToParent);
            // heightToParent = sibHeight > heightToParent ? 0 : heightToParent - sibHeight;
            // console.log('heightToParent2', parent.contentType, content.contentType, sibling.contentType, heightToParent);
        } else {
            content.y += sibling.y + sibling.h + sibling.mb;
            // heightToParent += content.mt + content.mb;
        }
    } else {
        content.y += parent.y;
    }
    content.h = content.h || 0;
    content.position.relative.position.expander(content);
    return content;
}

function mapFont(content, parent = {}) {
    // If font is a string, map from styles config
    if (typeof content.font === 'string') {
        content.font = styles.font[content.font];
    }
    let { font = {} } = content;
    // Also check if contentType has defaults to apply
    if (content.contentType) {
        const fontForType = styles.font[content.contentType];
        if (fontForType) {
            font = _.assign({}, fontForType, font);
        }
    }
    if (parent.font) {
        font = _.assign({}, parent.font, font);
    }

    const {
        family,
        lineHeight,
        size,
        weight,
        style
    } = _.assign({}, config.baseFont, font);

    // Font colours will inherit node colour unless font colour specified
    const colour = font.colour || content.colour || config.baseFont.colour;
    font.colour = mapColour({ colour });
    font.size = _.get(styles.fontSize, size) || +size;
    font.family = _.get(styles.fontFamily, family) || family;
    font.lineHeight = _.get(styles.lineHeightFactors, lineHeight) || +lineHeight;
    font.weight = _.get(styles.fontWeight, weight) || weight;
    font.style = _.get(styles.fontStyle, style) || style;
    font.type = font.weight;
    if (font.style !== 'normal') {
        if (font.type === 'normal') {
            font.type = font.style;
        } else {
            font.type += font.style;
        }
    }
    // TODO: Append the fontStyle to the font.type as well, and ensure that
    // TODO: the variant combo exists for installed fonts.
    content.font = font;
    return font;
}

// Additional styles that can be included in charts.  Could also be included in other elements
// in future
function mapStyles(stylesToMap) {
    const { fill, stroke, markdown, font, selectors } = stylesToMap;
    stylesToMap.markdown = markdown && mapColour({ colour: markdown });
    stylesToMap.fill = fill && mapColour({ colour: fill });
    stylesToMap.stroke = stroke && mapColour({ colour: stroke });
    stylesToMap.font = font && mapFont({ font });
    stylesToMap.selectors = selectors && _.mapValues(selectors, mapStyles);
    return stylesToMap;
}

function mapMarkdown(content) {
    const { markdown } = content;
    if (!markdown || typeof markdown !== 'object') {
        return content;
    }
    const { bold, italic, bolditalic, link } = markdown;
    markdown.bold = bold && mapFont({ font: { weight: 'bold', ...bold } }, content);
    markdown.italic = italic && mapFont({ font: { style: 'italic', ...italic } }, content);
    markdown.bolditalic = bolditalic && mapFont({ font: { style: 'italic', weight: 'bold', ...bolditalic } }, content);
    markdown.link = link && mapFont({ font: { style: 'italic', weight: 'bold', ...link } }, content);
    return markdown;
}

// Gets colours, fonts from the config.
function styleFromCfg(content, parent = {}) {
    if (!content || typeof content !== 'object') {
        return content;
    }
    // Font colours already done in content.font
    // This maps the element colour
    // Note: Only background/border actually use the colour right now.
    if (content.colour) {
        mapColour(content);
    } else if (parent.colour) {
        content.colour = parent.colour;
    }
    mapFont(content, parent);
    // if (content.font) {
    //     mapFont(content);
    // } else if (parent.font) {
    //     content.font = parent.font;
    // }

    if (content.styles) {
        mapStyles(content.styles);
    }
    if (content.markdown) {
        mapMarkdown(content);
    }

    return content;
}

function isAttribute(key) {
    return config.attributes.includes(key);
}

// If the node is a content node, put the type in so it will be rendered
function isContent(node, key) {
    // Content Nodes must be an object that is not at attribute
    if (!node || typeof node !== 'object' || isAttribute(key) || node instanceof Element) {
        return false;
    }
    node.contentType = key || 'root';
    return true;
}

// Takes a content node of a document template and recursively applies styles
// To the entire document tree
function applyStylesToTree(content, parent, sibling) {
    // Positions can be set explicitly, or by the tag type
    // eg. position = 'header' or a tag key called header are the same

    // Arrays themselves as containers don't have any style,
    // but can be used as containers
    if (!Array.isArray(content)) {
        content = styleFromCfg(content, parent, sibling);
    }
    positionContent(content, parent, sibling);
    let prevSibling = null;
    // Recurse the tree and style all nodes
    _.mapValues(content, (val, key) => {
        if (isContent(val, key)) {
            applyStylesToTree(val, content, prevSibling);
            // If content has a position, it is fixed/absolute so is no longer
            // a relative in its container.
            // This skips absolute positioned elements in same container
            if (val.position.relative) {
                prevSibling = val;
            }
        } else {
            // Borders, backgrounds, etc still have colours, sizes
            styleFromCfg(val);
        }
    });
    return content;
}

function applyStyles(root) {
    // Function that is called to start applying styles at a root node.
    // Clone the template and return a styled document
    // If there is anything specific to do for the root, it could go here.
    return applyStylesToTree(root);
}

// Utility to generate key-vals for the grid spacing
function gridSpacing(prefix, num, val, start = 0) {
    const entries = {};
    let startIndex = 0;
    if (start) {
        startIndex = 1;
    }
    for (let n = 0; n < num + 1; n++) {
        const label = `${prefix}-${n + startIndex}`;
        const space = n * val + start;
        entries[`${label}`] = space;
        // Add fractions down to 1/16
        for (let f = 1; f < 16; f += 2) {
            entries[`${label}-${f}/16`] = space + f * (val / 16);
            if (f < 8) {
                entries[`${label}-${f}/8`] = space + f * (val / 8);
            }
            if (f < 4) {
                entries[`${label}-${f}/4`] = space + f * (val / 4);
            }
            if (f < 2) {
                entries[`${label}-${f}/2`] = space + f * (val / 2);
            }
        }
    }
    // console.log('grid spacing:', prefix, num, val, start, entries);
    return entries;
}
// Utility for gridlines on the test page.
function gridLayout(grid = {}, { heights, widths } = config) {
    const colour = '#cccccc';
    const font = { colour, size: '2xs' };
    const h = mapSpacing('h-1');
    const w = mapSpacing('w-1');
    // w /= widths;
    for (let n = 1; n < heights; n++) {
        grid[`h${n}`] = {
            position: 'absolute',
            text: `h-${n}`,
            y: h * n,
            h,
            border: { colour, width: 1 },
            font,
            align: 'right'
        };
    }
    for (let n = 1; n < widths; n++) {
        grid[`w${n}`] = {
            position: 'absolute',
            text: `w-${n}`,
            x: w * n,
            w,
            border: { colour, width: 1 },
            font
        };
    }
    return grid;
}

// Add the styles that are calculated, based on config.
function init(cfg = defaults) {
    if (cfg.baseFont) {
        config.baseFont = mapFont({ font: cfg.baseFont });
    }
    _.assign(config, cfg);
    const {
        width,
        height,
        margin,
        footer,
        header,
        widths,
        heights,
        columns,
        gutter
    } = config;
    styles.position.default = styles.position.content;


    const columnWidth = (width - margin * 2 - (gutter * (columns - 1))) / columns;
    const w1 = columnWidth / widths;
    // const widthsPerCol = Math.floor(widths / columns);
    // const columnWidth = widthsPerCol * w1;
    const contentWidth = width - margin * 2;
    const contentHeight = height - header - footer;
    const h1 = contentHeight / heights;

    styles.spacing = {
        'w-col': columnWidth,
        'w-full': width,
        'w-content': contentWidth,
        ...gridSpacing('w', widths, w1),
        'h-full': height,
        'h-content': contentHeight,
        ...gridSpacing('h', heights, h1),
        'h-header': header,
        'h-footer': footer,
        'y-footer': height - footer,
        'y-bottom': height - 10,
        margin,
        gutter,
        // ...gridSpacing('x', widths, w1, x1),
        header,
        // ...gridSpacing('y', heights, h1, y1),
        ...gridSpacing('x-col', columns, columnWidth + gutter, margin)
    };
    pageLayout.grid = gridLayout(pageLayout.grid, config);
}

init();


// Default export gives you the config props and the styles
export default {
    ...config,
    styles,
    use: ({ calcTextHeight }) => {
        textHeight = calcTextHeight;
    }
};

// functions exported to apply styles, init config
export { applyStyles, init, pageLayout };
